import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const { language } = useLanguage();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const secondaryServices = language === 'fr' 
    ? ['Bâtiment & BTP', 'Immobilier', 'Services Techniques']
    : ['Building & Construction', 'Real Estate', 'Technical Services'];

  return (
    <section id="home" className="relative min-h-screen flex flex-col">
      {/* Top Section - Logo on clean background */}
      <div className="bg-white pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Logo in dedicated clean zone */}
            <div className="inline-block mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_role-build/artifacts/18ckonuq_WhatsApp%20Image%202026-01-26%20at%2019.46.30%20%281%29.jpeg"
                alt="KEYAN EXPERTISE"
                className="h-28 md:h-36 w-auto mx-auto"
              />
            </div>
            
            {/* Separator line */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-20 h-px bg-[#C9A227]"></div>
              <div className="w-2 h-2 bg-[#C9A227] rounded-full"></div>
              <div className="w-20 h-px bg-[#C9A227]"></div>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-3">
              {language === 'fr' ? 'Construction Métallique' : 'Metal Construction'}
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-[#C9A227] font-medium mb-6">
              {language === 'fr' ? 'Expertise Industrielle' : 'Industrial Expertise'}
            </p>

            {/* Secondary services */}
            <div className="flex flex-wrap justify-center gap-4 text-gray-500 text-sm">
              {secondaryServices.map((service, index) => (
                <span key={index} className="flex items-center gap-2">
                  {index > 0 && <span className="text-[#C9A227]">•</span>}
                  {service}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Image background with CTA */}
      <div className="flex-1 relative min-h-[400px]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80')`,
            }}
          />
          <div className="absolute inset-0 bg-[#1A1A1A]/70" />
        </div>

        {/* Content overlay */}
        <div className="relative h-full flex items-center justify-center py-16">
          <div className="text-center px-4">
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              {language === 'fr' 
                ? "Leader en conception, fabrication et montage de structures métalliques en Côte d'Ivoire."
                : "Leader in design, fabrication and assembly of metal structures in Ivory Coast."}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                onClick={() => scrollToSection('#contact')}
                className="bg-[#C9A227] hover:bg-[#B8922A] text-white px-8 py-6 text-lg transition-all duration-300 group"
              >
                {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('#about')}
                className="border-2 border-white text-white hover:bg-white hover:text-[#1A1A1A] px-8 py-6 text-lg transition-all duration-300"
              >
                {language === 'fr' ? 'En savoir plus' : 'Learn more'}
              </Button>
            </div>

            {/* Location */}
            <p className="mt-12 text-white/60 text-sm">
              Abidjan, Côte d'Ivoire
            </p>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-6 h-6 text-white/60" />
      </div>
    </section>
  );
};

export default HeroSection;